import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FeedbackService } from '../../../services/feedback.service';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogRef } from '@angular/cdk/dialog';
import { NotifiableError } from '@bugsnag/js';
import { BugsnagService } from '@cumlaude/bugsnag';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { FormsModule } from '@angular/forms';
import { BaseDialogComponent, DialogHeaderComponent } from '@cumlaude/shared-components-dialogs';
import { FilterService } from '../../../services/filter.service';
import { GroeperingService } from '../../../services/groepering.service';
import { attrLabel } from '../../../services/labels';
import { UserMenuService } from '../../../services/user-menu.service';
import { getBreadcrumbs } from '@cumlaude/shared-components-menu';

enum Indruk {
	HEEL_SLECHT = 'HEEL_SLECHT',
	SLECHT = 'SLECHT',
	MATIG = 'MATIG',
	GOED = 'GOED',
	HEEL_GOED = 'HEEL_GOED',
}

@Component({
	selector: 'app-feedback-dialog',
	templateUrl: './feedback-dialog.component.html',
	styleUrls: ['./feedback-dialog.component.scss'],
	imports: [BaseDialogComponent, DialogHeaderComponent, FormsModule, ButtonComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackDialogComponent {
	private readonly feedbackService = inject(FeedbackService);
	private readonly userMenuService = inject(UserMenuService);
	private readonly filterService = inject(FilterService);
	private readonly groeperingService = inject(GroeperingService);
	private readonly toastr = inject(ToastrService);
	private readonly bugsnag = inject(BugsnagService);
	protected readonly dialogRef = inject<DialogRef<void>>(DialogRef);

	indruk: Indruk | undefined;

	icons: { [option in Indruk]: string } = {
		HEEL_SLECHT: 'mad',
		SLECHT: 'not-amused',
		MATIG: 'neutral',
		GOED: 'amused',
		HEEL_GOED: 'happy',
	};

	labels: { [option in Indruk]: string } = {
		HEEL_SLECHT: 'heel slecht',
		SLECHT: 'slecht',
		MATIG: 'matig',
		GOED: 'goed',
		HEEL_GOED: 'heel goed',
	};

	options = Object.values(Indruk);

	done = signal(false);
	versturen = signal(false);

	opmerking = '';

	async send() {
		if (this.versturen()) return;
		this.versturen.set(true);

		try {
			// Indruk
			const label = this.indruk === undefined ? 'niet ingevuld' : this.labels[this.indruk];
			const title = `Indruk: ${label}`;

			// Dashboard
			const dashboard = this.userMenuService.dashboard();
			const breadcrumbs = dashboard ? getBreadcrumbs(dashboard) : '-';

			let filters = '-';
			if (this.filterService.isServiceRunning()) {
				filters = await firstValueFrom(
					this.filterService
						.getFilledFilters()
						.pipe(map((filters) => filters.map(([filter]) => this.filterService.configs[filter]!.label).join(', ')))
				);
			}

			const groeperingen = this.groeperingService
				.huidigeGroeperingen()
				.map((groepering) => attrLabel(groepering))
				.join(', ');

			await this.feedbackService.post({
				title,
				intro: this.opmerking,
				outro: [`Dashboard: ${breadcrumbs}`, `Filters: ${filters}`, `Groeperingen: ${groeperingen.length > 0 ? groeperingen : '-'}`].join('\n'),
			});
			this.done.set(true);
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error('Er ging iets fout bij het versturen van je feedback.');
		} finally {
			this.versturen.set(false);
		}
	}
}
